import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="post-harvest--harvest-containers"
// Manages dynamic container rows for the harvest step form.
// Each row captures: identifier, plants_count, net_weight_value, net_weight_unit.
// Serializes all rows to a hidden JSON field on every change.
export default class extends Controller {
  static targets = ["containerList", "template", "containersDataField", "totalWeight", "containerCount", "totalPlants"]
  static values = { counter: { type: Number, default: 0 } }

  connect() {
    this.identifierInputHandler = this.handleIdentifierInput.bind(this)
    this.containerListTarget.addEventListener("input", this.identifierInputHandler)
    this.reindex()
    this.serialize()
  }

  disconnect() {
    this.containerListTarget.removeEventListener("input", this.identifierInputHandler)
  }

  add(event) {
    event.preventDefault()
    this.counterValue++
    const content = this.templateTarget.innerHTML
      .replace(/NEW_ID/g, this.counterValue)
      .replace(/C-XX/g, this.formatIdentifier(this.counterValue))
    this.containerListTarget.insertAdjacentHTML("beforeend", content)
    this.serialize()
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest("[data-container-row]")
    if (row) {
      row.remove()
      this.reindex()
      this.serialize()
    }
  }

  update(event) {
    if (event?.target?.dataset?.field === "identifier") {
      this.updateIdentifierEditedState(event.target)
    }
    this.serialize()
  }

  handleIdentifierInput(event) {
    if (event.target?.dataset?.field === "identifier") {
      this.updateIdentifierEditedState(event.target)
      this.serialize()
    }
  }

  serialize() {
    const rows = this.containerListTarget.querySelectorAll("[data-container-row]")
    const containers = []
    let total = 0
    let totalPlants = 0

    rows.forEach((row) => {
      const identifier = row.querySelector("[data-field=identifier]")?.value || ""
      const netWeight = parseFloat(row.querySelector("[data-field=net_weight_value]")?.value) || 0
      const netUnit = row.querySelector("[data-field=net_weight_unit]")?.value || "g"
      const plantsCount = parseInt(row.querySelector("[data-field=plants_count]")?.value, 10) || null

      if (identifier && netWeight > 0) {
        containers.push({
          identifier,
          net_weight_value: netWeight,
          net_weight_unit: netUnit,
          plants_count: plantsCount
        })
        total += netWeight
        totalPlants += plantsCount || 0
      }
    })

    this.containersDataFieldTarget.value = JSON.stringify(containers)

    if (this.hasTotalWeightTarget) {
      const unit = rows.length > 0
        ? (rows[0].querySelector("[data-field=net_weight_unit]")?.value || "g")
        : "g"
      this.totalWeightTarget.textContent = `${total.toFixed(2)} ${unit}`
    }
    if (this.hasTotalPlantsTarget) {
      this.totalPlantsTarget.textContent = totalPlants
    }
    if (this.hasContainerCountTarget) {
      this.containerCountTarget.textContent = rows.length
    }
  }

  reindex() {
    const rows = this.containerListTarget.querySelectorAll("[data-container-row]")
    this.counterValue = rows.length
    rows.forEach((row, idx) => {
      const identifierField = row.querySelector("[data-field=identifier]")
      if (identifierField && !identifierField.dataset.edited) {
        identifierField.value = this.formatIdentifier(idx + 1)
      }
    })
  }

  updateIdentifierEditedState(identifierField) {
    const row = identifierField.closest("[data-container-row]")
    if (!row) {
      return
    }

    const rows = Array.from(this.containerListTarget.querySelectorAll("[data-container-row]"))
    const rowIndex = rows.indexOf(row)
    if (rowIndex < 0) {
      return
    }

    const defaultIdentifier = this.formatIdentifier(rowIndex + 1)
    const normalizedIdentifier = identifierField.value.trim().toUpperCase()

    if (normalizedIdentifier && normalizedIdentifier !== defaultIdentifier) {
      identifierField.dataset.edited = "true"
      return
    }

    delete identifierField.dataset.edited
  }

  formatIdentifier(number) {
    return `C-${String(number).padStart(2, "0")}`
  }
}
